import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Image } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import PageHeader from '../../components/PageHeader';
import Swal from 'sweetalert2';

const TeacherForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  /*  dropdown arrays  */
  const [programs, setPrograms] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [languages, setLanguages] = useState([]);

  /*  loading flags  */
  const [progLoading, setProgLoading] = useState(true);
  const [stateLoading, setStateLoading] = useState(true);
  const [cityLoading, setCityLoading] = useState(false);
  const [langLoading, setLangLoading] = useState(true);
  const AWS = process.env.REACT_APP_AWS_HOST;

  const instituteId = sessionStorage.getItem('instituteId');
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  // Teacher CRUD - Use the correct API and token
  const API = process.env.REACT_APP_API_URL;
  const Institute_token = sessionStorage.getItem('token');

  const LMS_HEADERS = {
    'Authorization': token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  const API_HEADERS = {
    'Authorization': `Bearer ${Institute_token}`,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const watchState = watch('state');

  // Fetch Programs
  const fetchPrograms = async () => {
    try {
      setProgLoading(true);
      const response = await fetch(`${LMS}/api/institutes/${instituteId}/programs`, {
        method: 'GET',
        headers: LMS_HEADERS
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const programOptions = result.data.map(p => ({ 
        value: p.id, 
        label: p.program_title 
      }));
      setPrograms(programOptions);
    } catch (error) {
      console.error('Error fetching programs:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to load programs.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setProgLoading(false);
    }
  };

  // Fetch States
  const fetchStates = async () => {
    try {
      setStateLoading(true);
      const response = await fetch(`${LMS}/api/get-state`, {
        method: 'GET',
        headers: LMS_HEADERS
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const stateOptions = result.data.map(s => ({ 
        value: s.id, 
        label: s.name 
      }));
      setStates(stateOptions);
    } catch (error) {
      console.error('Error fetching states:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to load states.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setStateLoading(false);
    }
  };

  // Fetch Cities based on State
  const fetchCities = async (stateId) => {
    try {
      setCityLoading(true);
      const response = await fetch(`${LMS}/api/get-city?state_id=${stateId}`, {
        method: 'GET',
        headers: LMS_HEADERS
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const cityOptions = result.data.map(c => ({ 
        value: c.id, 
        label: c.name 
      }));
      setCities(cityOptions);
    } catch (error) {
      console.error('Error fetching cities:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to load cities.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setCityLoading(false);
    }
  };

  // Fetch Languages
  const fetchLanguages = async () => {
    try {
      setLangLoading(true);
      const response = await fetch(`${LMS}/api/language`, {
        method: 'GET',
        headers: LMS_HEADERS
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const languageOptions = result.data.map(l => ({ 
        value: l.id, 
        label: l.language 
      }));
      setLanguages(languageOptions);
    } catch (error) {
      console.error('Error fetching languages:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to load languages.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setLangLoading(false);
    }
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Fetch Teacher Data for Edit Mode
  const fetchTeacherData = async (teacherId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/get-teacher?teacherId=${teacherId}`, {
        method: 'GET',
        headers: API_HEADERS
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const teacherData = result.data || result;
      const teacherDetail = teacherData.teacher_detail || {};

      // Map API response to form fields
      setValue('firstName', teacherData.first_name);
      setValue('lastName', teacherData.last_name);
      setValue('contactNumber', teacherData.contact_number);
      setValue('email', teacherData.email);
      
      // Fix gender capitalization
      const gender = teacherDetail.gender;
      if (gender) {
        setValue('gender', gender.charAt(0).toUpperCase() + gender.slice(1));
      }
      
      setValue('dateOfBirth', teacherDetail.date_of_birth);
      setValue('address1', teacherDetail.address1);
      setValue('address2', teacherDetail.address2 || '');
      setValue('profileSummary', teacherDetail.profile_summary);
      setValue('status', teacherData.status === 1 ? 'active' : 'inactive');

      // Set programs - handle string format "71" or "71,72,73"
      if (teacherDetail.program_ids) {
        const programIds = teacherDetail.program_ids.split(',').map(id => parseInt(id.trim()));
        const selectedPrograms = programs.filter(p => programIds.includes(p.value));
        setValue('programs', selectedPrograms);
      }

      // Set languages - from teacher_language array
      if (teacherData.teacher_language && Array.isArray(teacherData.teacher_language)) {
        const languageIds = teacherData.teacher_language.map(lang => lang.id);
        const selectedLanguages = languages.filter(l => languageIds.includes(l.value));
        setValue('languages', selectedLanguages);
      }

      // Set state and city
      if (teacherDetail.state) {
        const selectedState = states.find(s => s.value === teacherDetail.state);
        setValue('state', selectedState);
        
        // Fetch cities for the state and then set city
        if (teacherDetail.city) {
          await fetchCities(teacherDetail.state);
          
          // Wait for cities state to update, then set city
          // Use a small delay to ensure setCities has completed
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Now find and set the city from the fetched cities
          setCities(prevCities => {
            const selectedCity = prevCities.find(c => c.value === teacherDetail.city);
            if (selectedCity) {
              setValue('city', selectedCity);
            }
            return prevCities;
          });
        }
      }

      // Set profile image with proper URL
      if (teacherData.profile_image) {
        const imageUrl = `${AWS}${teacherData.profile_image}`;
        setPreview(imageUrl);
      }

      // Swal.fire({
      //   icon: 'success',
      //   title: 'Teacher data loaded successfully!',
      //   toast: true,
      //   position: 'top-end',
      //   showConfirmButton: false,
      //   timer: 3000
      // });

    } catch (error) {
      console.error('Error fetching teacher data:', error);
      Swal.fire({
        icon: 'error',
        title: `Failed to load teacher data: ${error.message}`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    if (instituteId) {
      fetchPrograms();
      fetchStates();
      fetchLanguages();
    }
  }, [instituteId]);

  // Load teacher data in edit mode
  useEffect(() => {
    if (isEdit && id && programs.length > 0 && states.length > 0 && languages.length > 0) {
      fetchTeacherData(id);
    }
  }, [isEdit, id, programs.length, states.length, languages.length]);

  // Load cities when state changes
  useEffect(() => {
    if (watchState?.value) {
      fetchCities(watchState.value);
      // Clear city selection when state changes
      setValue('city', null);
    } else {
      setCities([]);
    }
  }, [watchState]);

  const handleImagePick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const url = URL.createObjectURL(file);
    setPreview(url);
    
    // Convert to base64 for API
    try {
      const base64 = await convertToBase64(file);
      setValue('profileImageBase64', base64);
    } catch (error) {
      console.error('Error converting image to base64:', error);
      Swal.fire({
        icon: 'error',
        title: 'Failed to process image.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    }
  };

  // Create Teacher
  const createTeacher = async (teacherData) => {
    try {
      const response = await fetch(`${API}/api/teacher`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify(teacherData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating teacher:', error);
      throw error;
    }
  };

  // Update Teacher
  const updateTeacher = async (teacherId, teacherData) => {
    try {
      const response = await fetch(`${API}/api/teacher/${teacherId}`, {
        method: 'PUT',
        headers: API_HEADERS,
        body: JSON.stringify(teacherData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating teacher:', error);
      throw error;
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      // Format the data to match API requirements
      const formattedData = {
        first_name: data.firstName,
        last_name: data.lastName,
        contact_number: data.contactNumber,
        email: data.email,
        profile_image: data.profileImageBase64 || "",
        gender: data.gender,
        date_of_birth: data.dateOfBirth,
        institute_id: parseInt(instituteId),
        program_id: data.programs?.map(p => p.value) || [],
        state: data.state?.value,
        city: data.city?.value,
        address1: data.address1,
        address2: data.address2 || "",
        profile_summary: data.profileSummary,
        language_id: data.languages?.map(l => l.value) || [],
        status: data.status === 'active' ? 1 : 0
      };

      let response;
      
      if (isEdit) {
        response = await updateTeacher(id, formattedData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Teacher "${data.firstName} ${data.lastName}" updated successfully!`,
          timer: 1200,
          showConfirmButton: false
        });
      } else {
        response = await createTeacher(formattedData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Teacher "${data.firstName} ${data.lastName}" created successfully!`,
          timer: 1200,
          showConfirmButton: false
        });
      }

      if (response && (response.success || response.data || response.id)) {
        setTimeout(() => {
          navigate('/institute-teacher');
        }, 1200);
      } else {
        Swal.fire({
          icon: 'error',
          title: response?.message || 'Failed to save teacher',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        });
      }

    } catch (error) {
      console.error('Error saving teacher:', error);
      Swal.fire({
        icon: 'error',
        title: error.message || 'An error occurred while saving the teacher. Please try again.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Teacher' : 'Add Teacher'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/institute-dashboard' },
          { label: 'Teachers', path: '/institute-teacher' },
          { label: isEdit ? 'Edit' : 'Add', active: true },
        ]}
      />

      <Card className="skyblueborder">
        <Card.Header className="skyblue-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 gap-sm-0">
          <h5 className="mb-0 text-primary">{isEdit ? 'Update Teacher Details' : 'Enter New Teacher Details'}</h5>
          <Button
            variant="link"
            className="p-0 text-decoration-none"
            onClick={() => navigate('/institute-teacher')}
          >
            ← Back to Teacher List
          </Button>
        </Card.Header>
        <Card.Body>
          <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    {...register('firstName', { required: 'First name is required' })}
                    isInvalid={errors.firstName}
                  />
                  <Form.Control.Feedback type="invalid">{errors.firstName?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    {...register('lastName', { required: 'Last name is required' })}
                    isInvalid={errors.lastName}
                  />
                  <Form.Control.Feedback type="invalid">{errors.lastName?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Contact Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    {...register('contactNumber', {
                      required: 'Contact number is required',
                      pattern: { value: /^[0-9]{10}$/, message: 'Only numbers, exactly 10 digits' },
                    })}
                    isInvalid={errors.contactNumber}
                    maxLength={10}
                    disabled={isEdit}
                  />
                  <Form.Control.Feedback type="invalid">{errors.contactNumber?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^\S+@\S+$/i, message: 'Invalid email' },
                    })}
                    isInvalid={errors.email}
                    disabled={isEdit}
                  />
                  <Form.Control.Feedback type="invalid">{errors.email?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Profile Image</Form.Label>
                  <div className="d-flex align-items-center gap-3">
                    {preview && <Image src={preview} rounded width={90} height={90} style={{ objectFit: 'cover' }} />}
                    <Form.Control type="file" accept="image/*" onChange={handleImagePick} />
                  </div>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                  <div className="d-flex gap-3 mt-2">
                    <Form.Check
                      type="radio"
                      label="Male"
                      value="Male"
                      {...register('gender', { required: 'Gender is required' })}
                      isInvalid={!!errors.gender}
                    />
                    <Form.Check
                      type="radio"
                      label="Female"
                      value="Female"
                      {...register('gender', { required: 'Gender is required' })}
                      isInvalid={!!errors.gender}
                    />
                    <Form.Check
                      type="radio"
                      label="Other"
                      value="Other"
                      {...register('gender', { required: 'Gender is required' })}
                      isInvalid={!!errors.gender}
                    />
                  </div>
                  {errors.gender && <small className="text-danger">{errors.gender.message}</small>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    {...register('dateOfBirth', { required: 'DOB is required' })}
                    isInvalid={errors.dateOfBirth}
                    onFocus={(e) => e.target.showPicker?.()}
                  />
                  <Form.Control.Feedback type="invalid">{errors.dateOfBirth?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Programs (Multi-select) <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="programs"
                    control={control}
                    rules={{ validate: v => (v && v.length > 0) || 'Select at least one program' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti
                        options={programs}
                        classNamePrefix="select"
                        placeholder="Choose programs..."
                        isLoading={progLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.programs ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.programs ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                      />
                    )}
                  />
                  {errors.programs && <small className="text-danger">{errors.programs.message}</small>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Languages (Multi-select) <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="languages"
                    control={control}
                    rules={{ validate: v => (v && v.length > 0) || 'Select at least one language' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti
                        options={languages}
                        classNamePrefix="select"
                        placeholder="Choose languages..."
                        isLoading={langLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.languages ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.languages ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                      />
                    )}
                  />
                  {errors.languages && <small className="text-danger">{errors.languages.message}</small>}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Address Line 1 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    {...register('address1', { required: 'Address 1 is required' })}
                    isInvalid={errors.address1}
                  />
                  <Form.Control.Feedback type="invalid">{errors.address1?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Address Line 2</Form.Label>
                  <Form.Control type="text" {...register('address2')} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>State <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: 'State is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={states}
                        classNamePrefix="select"
                        placeholder="Select state..."
                        isLoading={stateLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.state ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.state ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                      />
                    )}
                  />
                  {errors.state && <small className="text-danger">{errors.state.message}</small>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>City <span className="text-danger">*</span></Form.Label>
                  <Controller
                    name="city"
                    control={control}
                    rules={{ required: 'City is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={cities}
                        classNamePrefix="select"
                        placeholder="Select city..."
                        isDisabled={!watchState}
                        isLoading={cityLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.city ? '#dc3545' : base.borderColor,
                            backgroundColor: errors.city ? '#fef2f2' : base.backgroundColor,
                          }),
                        }}
                      />
                    )}
                  />
                  {errors.city && <small className="text-danger">{errors.city.message}</small>}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Status <span className="text-danger">*</span></Form.Label>
                  <div className="d-flex gap-3 mt-2">
                    <Form.Check type="radio" label="Active" value="active" {...register('status', { required: 'Status is required' })} />
                    <Form.Check type="radio" label="Inactive" value="inactive" {...register('status', { required: 'Status is required' })} />
                  </div>
                  {errors.status && <small className="text-danger">{errors.status.message}</small>}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <Form.Group>
                  <Form.Label>Profile Summary <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    {...register('profileSummary', { required: 'Profile summary is required' })}
                    isInvalid={errors.profileSummary}
                  />
                  <Form.Control.Feedback type="invalid">{errors.profileSummary?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => navigate('/institute-teacher')}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2" />
                    {isEdit ? 'Update Teacher' : 'Save Teacher'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default TeacherForm;
